import { DataHistory, DataModel } from '@shared/models/data-model';
import { Creatable, Deletable, Editable } from '@/types/model.types';
import { PlatformUpdateApp } from '@data/_generated-api/platform-api/models/platform-update-app';
import { PlatformCreateApp } from '@data/_generated-api/platform-api/models/platform-create-app';
import { NavigationItemType } from '@shared/enum/navigation-item-type';
import { EgiPage } from '@ervo/page-framework';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import { Form } from '@common/formly/form.decorator';
import { egiAppCreateConfig } from '@/app/platform/platform-data/models/egi-app.entity.form-config';
import { EgiAppData } from '@/app/platform/platform-shared/models/types';

@Form('create', egiAppCreateConfig)
export class EgiAppEntity
  extends DataModel
  implements EgiAppData, Editable<PlatformUpdateApp>, Creatable<PlatformCreateApp>, Deletable<string>
{
  constructor(config?: Partial<EgiAppEntity> & Partial<DataHistory>) {
    super(config?.id, config);
    this.name = config?.name ?? '';
    this.machineName = config?.machineName ?? '';
    this.info = config?.info ?? '';
    this.rootNavigationItem = config?.rootNavigationItem ?? NavigationItemEntity.createDefault();
    this.poolName = config?.poolName ?? '';
  }

  public name: string;
  public machineName: string;
  public info: string;
  public rootNavigationItem: NavigationItemEntity;
  public poolName: string;

  readonly deleteKey = this.id;

  private _creationModel?: PlatformCreateApp;
  set creationModel(v) {
    this._creationModel = v;
  }

  get creationModel(): PlatformCreateApp {
    return {
      info: this.info,
      machineName: '',
      name: this.name,
      navigationItemName: this.rootNavigationItem.name,
      pageComponentId: this.rootNavigationItem.pageComponentId,
      poolName: ''
    };
  }

  get hasPoolName(): boolean {
    return !!this.poolName;
  }

  get hasMachineName(): boolean {
    return !!this.machineName;
  }

  get order(): number {
    return this.rootNavigationItem.order;
  }

  get hasConflicts(): boolean {
    return this.hasMissingDependencies || this.shouldBeSynced;
  }

  changeOrder(v: number) {
    this.rootNavigationItem.order = v;
  }

  get editingModel(): PlatformUpdateApp & { poolName: string } {
    return {
      id: this.id,
      info: this.info,
      machineName: '',
      name: this.name,
      pageComponentId: this.rootNavigationItem.pageComponentId,
      navigationItemName: this.rootNavigationItem.name,
      poolName: ''
    };
  }

  get hasRemovedPages(): boolean {
    return this.rootNavigationItem.asArray.some(
      i => i.lacksEgiPage && i.type !== NavigationItemType.Menu
    );
  }

  get hasMissingClickTroughPages(): boolean {
    return this.rootNavigationItem.asArray.some(i => i.hasMissingClickTroughPages);
  }

  get hasObsoleteClickTroughPages(): boolean {
    return this.rootNavigationItem.asArray.some(i => i.hasObsoleteClickThroughPages);
  }

  get shouldBeSynced(): boolean {
    return (
      this.rootNavigationItem.shouldBeSynced ||
      this.rootNavigationItem.hasDescendantThatShouldBeSynced
    );
  }

  get hasMissingDependencies(): boolean {
    return this.rootNavigationItem.asArray.some(i => !i.isCompatibleWith(this));
  }

  isCompatibleWith(page: EgiPage): boolean {
    if (!page) {
      return true;
    }

    const pageNeedsPoolName = !!page?.config.dependencies?.poolName;

    if (pageNeedsPoolName && !this.hasPoolName) {
      return false;
    }

    const needsMachineName = !!page.config.dependencies?.machineName;

    return !(needsMachineName && !this.hasMachineName);
  }

  public static createDefault(id?: string): EgiAppEntity {
    return new EgiAppEntity({
      id: id ?? '',
      name: '',
      poolName: '',
      info: '',
      rootNavigationItem: NavigationItemEntity.createDefault(),
      machineName: ''
    });
  }

  public static createWithId(id: string): EgiAppEntity {
    return this.createDefault(id);
  }
}
